import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchData } from '../../helpers/apiUtils';

/**
 * Werbetexte des Wiederholungs-Webinars (Doku .claude/docs/webinar-rerun-system.md § Stufe 2):
 * - Einladung je Mail (`einladung_1` Geschichte mit Betreff A/B, `einladung_2` Willkommen) — seit P2 (06.10.)
 *   schreibt die KI sie je Quelle; ein bearbeiteter Text ist ein Handtext und wird nie ersetzt.
 * - Rueckfall je Sendetag: „morgen"/„heute" (Heat-up zeitneutral umgeschrieben) — gehen raus, solange es fuer die
 *   Mail keinen freien Text gibt. Dazu der Nachfass.
 * Eine feste Regel prueft jeden Text. Nur `ok` geht raus; Bearbeiten speichert immer — mit Befund steht der Text
 * auf „gesperrt" und wird nicht versendet.
 */

const API = '/api/webinar-rerun-admin';

type Slot = 'einladung_1' | 'einladung_2' | 'morgen' | 'heute' | 'nachfass_gesehen' | 'nachfass_verpasst';
type CopyStatus = 'pending' | 'ok' | 'blocked' | 'error';

interface CopyFinding {
  art: string;
  feld: 'subject' | 'subject_b' | 'preview_text' | 'html_body';
  zitat: string;
  sperrt: boolean;
}

interface CopyView {
  id: number;
  slot: Slot;
  status: CopyStatus;
  origin: 'ai' | 'manual';
  subject: string | null;
  subject_b: string | null;
  prompt_version: string;
  preview_text: string | null;
  html_body: string | null;
  preview_subject: string | null;
  preview_subject_b: string | null;
  preview_html: string | null;
  findings: CopyFinding[];
  model: string | null;
  attempts: number;
  last_error: string | null;
  edited_by: string | null;
  edited_at: string | null;
  updated_at: string;
}

interface SourceCopies {
  webinar_id: string;
  title: string | null;
  start_date: string;
  /** Quelle hat eine geplante Sendung — fuer sie schreibt die KI die Einladung je Mail. */
  geplant: boolean;
  /** Welcher Text je Mail rausgeht: der Text je Mail, der Rueckfall auf „heute"/„morgen", oder gar keiner. */
  versand: {
    mail1: 'einladung_1' | 'rueckfall' | 'nur_heute' | 'nur_morgen' | 'kein_text';
    mail2: 'einladung_2' | 'rueckfall' | 'kein_text';
  };
  slots: { slot: Slot; heatup_id: number | null; copy: CopyView | null }[];
}

const INVITE_SLOTS: Slot[] = ['einladung_1', 'einladung_2'];

const SLOT_LABEL: Record<Slot, string> = {
  einladung_1: 'Mail 1 · Geschichte (Betreff A/B), sofort bzw. 16:00 Uhr',
  einladung_2: 'Mail 2 · Willkommen, am Sendetag 16:00 Uhr',
  morgen: 'Rückfall „morgen“ · 1. Einladung ab 17:00 Uhr',
  heute: 'Rückfall „heute“ · 1. Einladung 7–17 Uhr, 2. Einladung 16:00 Uhr',
  nachfass_gesehen: 'Nachfass „gesehen" · Folgetag 16:00 Uhr, Link zur Bestellseite',
  nachfass_verpasst: 'Nachfass „verpasst" · Folgetag 16:00 Uhr, Link zur heutigen Wiederholung (ohne Sendung keine Mail)',
};

const ART_LABEL: Record<string, string> = {
  datum: 'Datum',
  wochentag: 'Wochentag',
  uhrzeit: 'Uhrzeit',
  live: '„live"',
  premiere: 'Premiere',
  knappheit: 'Knappheit',
  bonus: 'Bonus',
  frist: 'Frist',
  einmalig: 'Einmalig/keine Aufzeichnung',
  fragen: 'Fragen/Chat',
  stunden: 'Zeitspanne („wenige Stunden")',
  dauer: 'Feste Dauer',
  link: 'Link',
  platzhalter: 'Platzhalter',
  leer: 'Leer',
  laenge: 'Länge',
  termin: 'Termin fehlt („um {WEBINAR_UHRZEIT} Uhr")',
  sofort: 'Verspricht „jetzt ansehen"',
  pflicht: 'Pflichtfeld',
  html: 'Markup',
  fakt: 'Zahl nicht im Material',
  rendite: 'Rendite im Betreff',
  absender: 'Referent',
};

const FELD_LABEL: Record<string, string> = { subject: 'Betreff', subject_b: 'Betreff B', preview_text: 'Vorschau', html_body: 'Text' };

function statusChip(copy: CopyView | null, heatupId: number | null, slot: Slot): { label: string; color: 'success' | 'error' | 'warning' | 'default' } {
  // Die Einladung je Mail hat keine Heat-up-Vorlage — sie entsteht aus dem ganzen Material der Quelle.
  if (!heatupId && !INVITE_SLOTS.includes(slot)) return { label: 'kein passender Heat-up', color: 'default' };
  if (!copy) return { label: 'noch nicht erzeugt', color: 'default' };
  if (copy.status === 'ok') return { label: copy.origin === 'manual' ? 'frei (Handtext)' : 'frei', color: 'success' };
  if (copy.status === 'blocked') return { label: 'gesperrt', color: 'error' };
  if (copy.status === 'error') return { label: 'KI gescheitert', color: 'error' };
  return { label: 'in Arbeit', color: 'warning' };
}

function formatBerlin(s: string | null | undefined): string {
  if (!s) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : s;
}

interface EditState {
  copy: CopyView;
  webinarId: string;
  subject: string;
  /** Nur Mail 1 (Betreff-Test); fuer alle anderen Slots wird das Feld nicht mitgeschickt. */
  subject_b: string;
  preview_text: string;
  html_body: string;
}

type TestArt = { mailNr: 1 | 2; slot: 'morgen' | 'heute'; label: string };
const TEST_ARTEN: TestArt[] = [
  { mailNr: 1, slot: 'morgen', label: 'Mail 1 · morgen' },
  { mailNr: 1, slot: 'heute', label: 'Mail 1 · heute' },
  { mailNr: 2, slot: 'heute', label: 'Mail 2 · heute' },
];

const RerunCopiesSection: React.FC = () => {
  const [sources, setSources] = useState<SourceCopies[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [editFindings, setEditFindings] = useState<CopyFinding[] | null>(null);
  const [testAiring, setTestAiring] = useState('');
  const [testBetreff, setTestBetreff] = useState<'a' | 'b'>('a');
  const seqRef = useRef(0);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    const r = await fetchData<SourceCopies[]>(`${API}/copies`);
    if (!aliveRef.current || seq !== seqRef.current) return;
    if (!r.success || !r.data) {
      setError(r.error || 'Werbetexte konnten nicht geladen werden');
      return;
    }
    setError(null);
    setSources(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const regenerate = async (copy: CopyView) => {
    setBusy(`regen-${copy.id}`);
    setMessage(null);
    const r = await fetchData(`${API}/copies/${copy.id}/regenerate`, { method: 'POST' });
    if (!aliveRef.current) return;
    setBusy(null);
    const einladung = INVITE_SLOTS.includes(copy.slot);
    setMessage(
      r.success
        ? {
            text: einladung
              ? 'Neue Fassung angelegt — der nächste Lauf (alle 15 Minuten) schreibt sie. Bis dahin geht für diese Mail der Rückfall-Text raus; die bisherige Fassung bleibt gespeichert.'
              : 'Neu-Generierung angestoßen — der nächste Lauf (alle 15 Minuten) schreibt den Text neu. Bis dahin geht keine Einladung raus.',
            severity: 'info',
          }
        : { text: r.error || 'Neu-Generierung fehlgeschlagen', severity: 'error' },
    );
    await load();
  };

  const sendTest = async (art: TestArt) => {
    const key = `test-${art.mailNr}-${art.slot}`;
    setBusy(key);
    setMessage(null);
    const r = await fetchData<{ email: string; esp_campaign_id: number; subject: string }>(`${API}/copies/test-invite`, {
      method: 'POST',
      body: JSON.stringify({ airing_id: testAiring.trim(), slot: art.slot, mail_nr: art.mailNr, betreff: testBetreff }),
    });
    if (!aliveRef.current) return;
    setBusy(null);
    setMessage(
      r.success && r.data
        ? {
            text: `Test „${art.label}“, Betreff ${testBetreff.toUpperCase()} („${r.data.subject}“) an ${r.data.email} verschickt (Iterable-Kampagne ${r.data.esp_campaign_id}, Versand in ~3 Minuten).`,
            severity: 'success',
          }
        : { text: r.error || 'Test-Einladung fehlgeschlagen', severity: 'error' },
    );
  };

  const openEdit = (copy: CopyView, webinarId: string) => {
    setEditFindings(null);
    setEdit({
      copy,
      webinarId,
      subject: copy.subject || '',
      subject_b: copy.subject_b || '',
      preview_text: copy.preview_text || '',
      html_body: copy.html_body || '',
    });
  };

  const saveEdit = async () => {
    if (!edit) return;
    setBusy('edit');
    // Betreff B nur bei Mail 1 mitschicken — fehlt das Feld, laesst der Server die Spalte unveraendert.
    const mitB = edit.copy.slot === 'einladung_1' ? { subject_b: edit.subject_b } : {};
    const r = await fetchData<{ copy: CopyView; findings: CopyFinding[] }>(`${API}/copies/${edit.copy.id}`, {
      method: 'PUT',
      body: JSON.stringify({ subject: edit.subject, preview_text: edit.preview_text, html_body: edit.html_body, ...mitB }),
    });
    if (!aliveRef.current) return;
    setBusy(null);
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Speichern fehlgeschlagen', severity: 'error' });
      return;
    }
    const sperrend = r.data.findings.filter((f) => f.sperrt);
    if (sperrend.length) {
      // Dialog offen lassen: gespeichert, aber gesperrt — die Befunde stehen direkt im Dialog.
      setEditFindings(r.data.findings);
      setEdit({ ...edit, copy: r.data.copy });
      setMessage({ text: `Gespeichert, aber gesperrt (${sperrend.length} Befund(e)) — wird nicht versendet.`, severity: 'error' });
    } else {
      setEdit(null);
      setMessage({ text: 'Gespeichert und freigegeben.', severity: 'success' });
    }
    await load();
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h6">Werbetexte (Welcome-Einladung)</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => load()}>
          Aktualisieren
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Je Webinar mit geplanter Sendung schreibt die KI Mail 1 (Geschichte, zwei Betreffe im Test) und Mail 2
        (Willkommen) aus LP, Erinnerungen, Heat-ups und Raum-Angebot. Fehlt ein freier Text je Mail, geht der
        Rückfall-Text „heute“/„morgen“ raus. Eine feste Prüfung sperrt Termin-, Live-, Premieren-, Bonus- und
        Knappheitsaussagen sowie Zahlen, die nicht im Material stehen. Bearbeiten macht einen Text zum Handtext — den
        ersetzt die KI nie. Platzhalter{' {WEBINAR_TAG_RELATIV} / {WEBINAR_UHRZEIT} / {WEBINAR_LINK} '}werden beim
        Versand gefüllt.
      </Typography>
      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={2}>
        <TextField
          size="small"
          label="Sendung für Test-Einladung"
          placeholder="Kennung, z. B. aus „Test-Sendung“ unten"
          value={testAiring}
          onChange={(e) => setTestAiring(e.target.value)}
          inputProps={{ maxLength: 10 }}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label="Betreff"
          value={testBetreff}
          onChange={(e) => setTestBetreff(e.target.value === 'b' ? 'b' : 'a')}
          sx={{ minWidth: 110 }}
        >
          <MenuItem value="a">A</MenuItem>
          <MenuItem value="b">B (nur Mail 1)</MenuItem>
        </TextField>
        {TEST_ARTEN.map((art) => {
          const key = `test-${art.mailNr}-${art.slot}`;
          return (
            <Button
              key={key}
              size="small"
              variant="outlined"
              disabled={busy !== null || !/^[A-Za-z0-9]{10}$/.test(testAiring.trim()) || (art.mailNr === 2 && testBetreff === 'b')}
              onClick={() => sendTest(art)}
              startIcon={busy === key ? <CircularProgress size={14} /> : undefined}
            >
              Test „{art.label}“ an mich
            </Button>
          );
        })}
      </Stack>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {!sources && !error && <CircularProgress size={20} />}
      {sources && sources.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Kein Webinar mit fertigem Raum-Video und Untertiteln im Zeitfenster.
        </Typography>
      )}
      <Stack spacing={2}>
        {(sources || []).map((src) => (
          <Box key={src.webinar_id} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {src.webinar_id} · {src.title || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Original: {formatBerlin(src.start_date)}
              {src.geplant ? ' · Sendung geplant' : ''}
            </Typography>
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
              {([1, 2] as const).map((nr) => {
                const v = nr === 1 ? src.versand.mail1 : src.versand.mail2;
                const VERSAND_LABEL: Record<string, string> = {
                  einladung_1: 'Text je Mail',
                  einladung_2: 'Text je Mail',
                  rueckfall: nr === 1 ? 'Rückfall „heute“/„morgen“' : 'Rückfall „heute“',
                  nur_heute: 'Rückfall nur „heute“ — abends keine Mail 1',
                  nur_morgen: 'Rückfall nur „morgen“ — tagsüber keine Mail 1',
                  kein_text: 'keine — es gibt keinen freien Text',
                };
                const farbe = v.startsWith('einladung') ? 'success' : v === 'rueckfall' ? 'warning' : 'error';
                return (
                  <Chip key={nr} size="small" variant="outlined" color={farbe} label={`Mail ${nr} verschickt: ${VERSAND_LABEL[v] ?? v}`} />
                );
              })}
            </Stack>
            {[src.slots.filter((s) => INVITE_SLOTS.includes(s.slot)), src.slots.filter((s) => !INVITE_SLOTS.includes(s.slot))].map((gruppe, gi) => (
            <Stack key={gi} direction={{ xs: 'column', lg: 'row' }} spacing={2} mt={gi === 0 ? 1 : 3}>
              {gruppe.map(({ slot, heatup_id, copy }) => {
                const chip = statusChip(copy, heatup_id, slot);
                const sperrend = (copy?.findings || []).filter((f) => f.sperrt);
                const hinweise = (copy?.findings || []).filter((f) => !f.sperrt);
                return (
                  <Box key={slot} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600}>
                        {SLOT_LABEL[slot]}
                      </Typography>
                      <Chip size="small" label={chip.label} color={chip.color} />
                      {heatup_id && (
                        <Typography variant="caption" color="text.secondary">
                          Quelle: Heat-up {heatup_id}
                        </Typography>
                      )}
                    </Stack>
                    {copy?.status === 'error' && copy.last_error && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        {copy.attempts} Versuche. Letzter Fehler: {copy.last_error}
                      </Alert>
                    )}
                    {sperrend.length > 0 && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        {sperrend.slice(0, 8).map((f, i) => (
                          <div key={i}>
                            <strong>{ART_LABEL[f.art] || f.art}</strong> ({FELD_LABEL[f.feld] || f.feld}): „{f.zitat}“
                          </div>
                        ))}
                      </Alert>
                    )}
                    {hinweise.length > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        Hinweise: {hinweise.map((f) => `${ART_LABEL[f.art] || f.art} („${f.zitat.slice(0, 50)}")`).join(' · ')}
                      </Typography>
                    )}
                    {copy?.preview_html && (
                      <>
                        <Typography variant="body2" mb={0.5}>
                          <strong>{copy.preview_subject_b ? 'Betreff A' : 'Betreff'}:</strong> {copy.preview_subject}
                        </Typography>
                        {copy.preview_subject_b && (
                          <Typography variant="body2" mb={0.5}>
                            <strong>Betreff B:</strong> {copy.preview_subject_b}
                          </Typography>
                        )}
                        <Box
                          component="iframe"
                          title={`Vorschau ${src.webinar_id} ${slot}`}
                          sandbox=""
                          srcDoc={copy.preview_html}
                          sx={{ width: '100%', height: 360, border: '1px solid #e0e0e0', bgcolor: '#ffffff' }}
                        />
                      </>
                    )}
                    {copy && (
                      <Stack direction="row" spacing={1} mt={1} alignItems="center" flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy !== null || !copy.html_body}
                          onClick={() => openEdit(copy, src.webinar_id)}
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          size="small"
                          disabled={busy !== null}
                          onClick={() => regenerate(copy)}
                          startIcon={busy === `regen-${copy.id}` ? <CircularProgress size={14} /> : undefined}
                        >
                          Neu generieren
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          {copy.origin === 'manual'
                            ? `bearbeitet von ${copy.edited_by || '—'} am ${formatBerlin(copy.edited_at)}`
                            : `${copy.model || 'KI'} · ${formatBerlin(copy.updated_at)}`}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
            ))}
          </Box>
        ))}
      </Stack>

      <Dialog open={Boolean(edit)} onClose={() => (busy === 'edit' ? undefined : setEdit(null))} maxWidth="lg" fullWidth>
        <DialogTitle>
          Werbetext bearbeiten · {edit?.webinarId} · {edit ? SLOT_LABEL[edit.copy.slot] : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {editFindings && editFindings.some((f) => f.sperrt) && (
              <Alert severity="error">
                {editFindings
                  .filter((f) => f.sperrt)
                  .slice(0, 10)
                  .map((f, i) => (
                    <div key={i}>
                      <strong>{ART_LABEL[f.art] || f.art}</strong> ({FELD_LABEL[f.feld] || f.feld}): „{f.zitat}“
                    </div>
                  ))}
              </Alert>
            )}
            <TextField
              label={edit?.copy.slot === 'einladung_1' ? 'Betreff A' : 'Betreff'}
              size="small"
              value={edit?.subject || ''}
              onChange={(e) => edit && setEdit({ ...edit, subject: e.target.value })}
              inputProps={{ maxLength: 500 }}
            />
            {edit?.copy.slot === 'einladung_1' && (
              <TextField
                label="Betreff B (Betreff-Test)"
                size="small"
                value={edit.subject_b}
                onChange={(e) => setEdit({ ...edit, subject_b: e.target.value })}
                inputProps={{ maxLength: 500 }}
                helperText="Leer = kein Betreff-Test, alle bekommen Betreff A. Gleiche Prüfung wie Betreff A."
              />
            )}
            <TextField
              label="Vorschautext"
              size="small"
              value={edit?.preview_text || ''}
              onChange={(e) => edit && setEdit({ ...edit, preview_text: e.target.value })}
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              label="HTML"
              multiline
              minRows={14}
              maxRows={28}
              value={edit?.html_body || ''}
              onChange={(e) => edit && setEdit({ ...edit, html_body: e.target.value })}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
              helperText="{WEBINAR_TAG_RELATIV} = morgen/heute · {WEBINAR_TAG_RELATIV_GROSS} = Morgen/Heute · {WEBINAR_UHRZEIT} = Sendebeginn · {WEBINAR_LINK} = persönlicher Link (Einladung: Raum · Nachfass: Bestellseite bzw. Sendung) — Pflicht"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={busy === 'edit'}>
            Schließen
          </Button>
          <Button
            variant="contained"
            onClick={saveEdit}
            disabled={busy === 'edit' || !edit?.subject.trim() || !edit?.html_body.trim()}
            startIcon={busy === 'edit' ? <CircularProgress size={14} /> : undefined}
          >
            Speichern und prüfen
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RerunCopiesSection;
